<template>
  <div :class="{
      page: true,
      'page-home': true,
    }" v-if="cubeStore.isCubeSelected" :style="colorStyles">

    <MeterRead :negative-color="negativeColor" :positive-color="positiveColor" />
    <Dots :negative-color="negativeColor" :positive-color="positiveColor" />

    <div class="consumptions">
      <ConsumptionFrom type="community" :negative-color="negativeColor" :positive-color="positiveColor" />
      <ConsumptionFrom type="supplier" :negative-color="negativeColor" :positive-color="positiveColor" />
    </div>

    <CubeNotReachablePopup v-if="!isCubeReachable || isCommunityless" />
    <MeterNotReachablePopup v-if="isSimulationMeasure && !meterless" />

    <p class="page-home__meterless" v-if="meterless">
      {{ t('global.meterlessNoteText') }}
    </p>

    <div :class="{
      'page-home__overlay': true,
      'page-home__overlay--visible': overlayVisible,
    }"></div>
  </div>
</template>

<script setup>
  import Dots from '@/components/consumption/Dots.vue'
  import MeterRead from '@/components/consumption/MeterRead.vue'
  import ConsumptionFrom from '@/components/consumption/ConsumptionFrom.vue'
  import { useRouter } from 'vue-router'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed, onMounted, watch } from 'vue'
  import { useSpecificPopup } from '@/composables/useSpecificPopup.ts'
  import MeterNotReachablePopup from '@/components/popups/MeterNotReachablePopup.vue'
  import CubeNotReachablePopup from '@/components/popups/CubeNotReachablePopup.vue'
  import { usePopupOverlay } from '@/composables/usePopupOverlay.ts'
import { useColors } from '@/composables/useColors';
import { useAccountStore } from '@/stores/useAccountStore';
import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore';
import { useCubeStore } from '@/stores/useCubeStore';

  const router = useRouter()
  const accountStore = useAccountStore()
  const cubeStore = useCubeStore()
  const currentReadsStore = useCurrentReadsStore()
    const {
    colorPrimary,
    colorSecondary,
    colorSecondaryHighContrast
  } = useColors();
  const { t } = useStrings()
  const cubeNotReachablePopup = useSpecificPopup('cubeNotReachable')
  const { overlayVisible } = usePopupOverlay('home')

  onMounted(() => {
    if (accountStore.isDemoUser) {
      router.replace({
        name: 'choose-provider',
        params: { context: 'home' },
      })
    }
  })

  const colorStyles = computed(() => {
    return `
      --color-positive-value: ${positiveColor.value};
      --color-negative-value: ${negativeColor.value};
    `
  });

  const positiveColor = computed(() => {
    return colorPrimary.value;
  });

  const negativeColor = computed(() => {
    if (accountStore.isHighContrast) {
      return colorSecondaryHighContrast.value;
    } else {
      return colorSecondary.value;
    }
  });

  const isCommunityless = computed(() => accountStore.properties.isCommunityless)
  const isCubeReachable = computed(() => currentReadsStore.isCubeReachable)
  const meterless = computed(() => cubeStore.meterType === 'Meterless')
  const isSimulationMeasure = computed(() => currentReadsStore.isSimulationMeasure)
</script>

<style lang="scss">
  .app--home .layout__content {
    // To allow centering the page vertically without using position absolute.
    // Position absolute would prevent the popup overlays from filling the whole
    // height without the header and bottom navigation.
    display: flex;
    align-items: center;
  }

  .page-home {
    margin: auto 0;
    width: 100%;

    &__overlay {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.7);
      transition: $transition-base;

      z-index: $popup-overlay-z-index;
      visibility: hidden;

      &--visible {
        visibility: visible;
        opacity: 1;
      }
    }
  }

  .page-home__message {
    white-space: pre-line;
  }

  .page-home__meterless {
    margin-top: $spacer10;
    text-align: center;
  }

  .consumptions {
    // The wrapper for the 2 consumption components.
    display: flex;
    justify-content: center;

    .consumption-from {
      flex-basis: 45%;
      max-width: 180px;
      transition: $transition-base;

      @media (min-height: 1000px) and (min-width: $bp-md) {
        max-width: 220px;
      }

      &:first-child {
        margin-right: 2%;

        @media (min-height: 700px) and (min-width: $bp-ssm) {
          margin-right: 7%;
        }
      }

      &:last-child {
        margin-left: 2%;

        @media (min-height: 700px) and (min-width: $bp-ssm) {
          margin-left: 7%;
        }
      }
    }
  }
</style>
